import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { Plus, Check, Pencil, Trash2, Split } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useProjectPayments,
  useProjectPaymentSummary,
  useMarkPaymentAsPaid,
  useDeletePayment,
  Payment,
} from '@/hooks/usePayments'
import { PaymentDialog } from './PaymentDialog'
import { InstallmentsDialog } from './InstallmentsDialog'
import { PaymentStatusBadge } from './PaymentStatusBadge'
import { PaymentMethodIcon } from './PaymentMethodIcon'

interface ProjectPaymentsSectionProps {
  projectId: string
  projectName: string
  projectValue: number | null
  billingType: string | null
}

export function ProjectPaymentsSection({
  projectId,
  projectName,
  projectValue,
  billingType,
}: ProjectPaymentsSectionProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [installmentsOpen, setInstallmentsOpen] = useState(false)
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  const { data: payments, isLoading } = useProjectPayments(projectId)
  const { data: summary } = useProjectPaymentSummary(projectId)
  const markAsPaid = useMarkPaymentAsPaid()
  const deletePayment = useDeletePayment()

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    }).format(value)
  }

  const totalPaid = summary?.total_paid || 0
  const totalPending = summary?.total_pending || 0
  const totalOverdue = summary?.total_overdue || 0
  const total = totalPaid + totalPending + totalOverdue

  const paymentProgress = projectValue && projectValue > 0
    ? (totalPaid / projectValue) * 100
    : total > 0
      ? (totalPaid / total) * 100
      : 0

  const handleEdit = (payment: Payment) => {
    setEditingPayment(payment)
    setDialogOpen(true)
  }

  const handleDelete = async () => {
    if (deleteConfirmId) {
      await deletePayment.mutateAsync(deleteConfirmId)
      setDeleteConfirmId(null)
    }
  }

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open)
    if (!open) {
      setEditingPayment(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">💰 Pagamentos</CardTitle>
          <div className="flex gap-2">
            {billingType === 'fixed' && projectValue && projectValue > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setInstallmentsOpen(true)}
              >
                <Split className="h-4 w-4 mr-1" />
                Parcelar
              </Button>
            )}
            <Button size="sm" onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Adicionar
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Valor Total</p>
              <p className="text-lg font-bold">
                {formatCurrency(projectValue || total)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Recebido</p>
              <p className="text-lg font-bold text-green-600">
                {formatCurrency(totalPaid)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Pendente</p>
              <p className="text-lg font-bold text-yellow-600">
                {formatCurrency(totalPending)}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Atrasado</p>
              <p className="text-lg font-bold text-red-600">
                {formatCurrency(totalOverdue)}
              </p>
            </div>
          </div>

          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-muted-foreground">Progresso de recebimento</span>
              <span className="font-medium">{Math.round(paymentProgress)}%</span>
            </div>
            <Progress value={paymentProgress} className="h-3" />
          </div>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Histórico de Pagamentos</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
            </div>
          ) : payments && payments.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Status</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead>Vencimento</TableHead>
                    <TableHead>Forma</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>
                        <PaymentStatusBadge
                          status={payment.status}
                          size="sm"
                          pulse={payment.status === 'overdue'}
                        />
                      </TableCell>
                      <TableCell className="max-w-[150px]">
                        <p className="truncate">{payment.description || '-'}</p>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(Number(payment.amount))}
                      </TableCell>
                      <TableCell>
                        {payment.due_date
                          ? format(parseISO(payment.due_date), 'dd/MM/yyyy')
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <PaymentMethodIcon method={payment.payment_method} showLabel={false} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          {(payment.status === 'pending' || payment.status === 'overdue') && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-green-600 hover:text-green-700 hover:bg-green-50"
                              onClick={() => markAsPaid.mutate(payment.id)}
                              disabled={markAsPaid.isPending}
                            >
                              <Check className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => handleEdit(payment)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => setDeleteConfirmId(payment.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p>Nenhum pagamento registrado</p>
              <Button
                variant="link"
                className="mt-2"
                onClick={() => setDialogOpen(true)}
              >
                Adicionar primeiro pagamento
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Dialog */}
      <PaymentDialog
        open={dialogOpen}
        onOpenChange={handleDialogClose}
        payment={editingPayment}
        defaultProjectId={projectId}
      />

      {/* Installments Dialog */}
      {billingType === 'fixed' && projectValue && projectValue > 0 && (
        <InstallmentsDialog
          open={installmentsOpen}
          onOpenChange={setInstallmentsOpen}
          projectId={projectId}
          projectName={projectName}
          totalValue={projectValue}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir pagamento?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
